import { Component, EventEmitter, Input, Output, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService } from '../../../core/services/api';
import { NotificationService } from '../../../core/services/notification';

@Component({
  selector: 'app-event-booking-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule],
  template: `
    <div class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" (click)="closeModal()"></div>

      <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div class="relative transform rounded-2xl bg-white text-left shadow-xl transition-all w-full mb-4 sm:my-8 sm:w-full sm:max-w-lg" (click)="$event.stopPropagation()">
          
          <!-- Header -->
          <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4 rounded-t-2xl">
            <div class="sm:flex sm:items-start">
              <div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-orange-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg class="h-6 w-6 text-enduro-orange" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                </svg>
              </div>
              <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                <h3 class="text-base font-semibold leading-6 text-gray-900" id="modal-title">
                    {{ 'EVENTS.BOOKING.TITLE' | translate }}
                </h3>
                <button (click)="closeModal()" class="absolute top-4 right-4 text-gray-400 hover:text-enduro-orange transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <div class="mt-2">
                  <p class="text-sm text-gray-500 font-bold">{{ event?.title_it }}</p>
                  <p class="text-xs text-gray-400">{{ event?.event_date | date:'longDate' }}</p>
                </div>

                <!-- Form -->
                <div class="mt-6">
                  <form [formGroup]="bookingForm" (ngSubmit)="onSubmit()" class="space-y-4">
                    
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Name -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">
                                {{ 'EVENTS.BOOKING.NAME' | translate }} <span class="text-red-600">*</span>
                            </label>
                            <input 
                                type="text" 
                                formControlName="name"
                                [placeholder]="'EVENTS.BOOKING.PLACEHOLDERS.NAME' | translate"
                                [class.border-red-500]="bookingForm.get('name')?.invalid && bookingForm.get('name')?.touched"
                                class="w-full h-11 rounded-lg border-2 border-gray-300 shadow-sm sm:text-sm px-3 transition-colors focus:outline-none focus:ring-2 focus:ring-enduro-orange"
                            >
                            @if (bookingForm.get('name')?.invalid && bookingForm.get('name')?.touched) {
                                <p class="text-xs text-red-600 mt-1">
                                    @if (bookingForm.get('name')?.errors?.['required']) {
                                        {{ 'EVENTS.BOOKING.ERRORS.NAME_REQUIRED' | translate }}
                                    }
                                    @if (bookingForm.get('name')?.errors?.['minlength']) {
                                        {{ 'EVENTS.BOOKING.ERRORS.NAME_LENGTH' | translate }}
                                    }
                                    @if (bookingForm.get('name')?.errors?.['hasNumbers']) {
                                        {{ 'EVENTS.BOOKING.ERRORS.NAME_NO_NUMBERS' | translate }}
                                    }
                                </p>
                            }
                        </div>

                        <!-- Email -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">
                                {{ 'EVENTS.BOOKING.EMAIL' | translate }} <span class="text-red-600">*</span>
                            </label>
                            <input 
                                type="email" 
                                formControlName="email"
                                [placeholder]="'EVENTS.BOOKING.PLACEHOLDERS.EMAIL' | translate"
                                [class.border-red-500]="bookingForm.get('email')?.invalid && bookingForm.get('email')?.touched"
                                class="w-full h-11 rounded-lg border-2 border-gray-300 shadow-sm sm:text-sm px-3 transition-colors focus:outline-none focus:ring-2 focus:ring-enduro-orange"
                            >
                            @if (bookingForm.get('email')?.invalid && bookingForm.get('email')?.touched) {
                                <p class="text-xs text-red-600 mt-1">
                                    @if (bookingForm.get('email')?.errors?.['required']) {
                                        {{ 'EVENTS.BOOKING.ERRORS.EMAIL_REQUIRED' | translate }}
                                    }
                                    @if (bookingForm.get('email')?.errors?.['email']) {
                                        {{ 'EVENTS.BOOKING.ERRORS.EMAIL_INVALID' | translate }}
                                    }
                                </p>
                            }
                        </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Phone -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">
                                {{ 'EVENTS.BOOKING.PHONE' | translate }} <span class="text-red-600">*</span>
                            </label>
                            <input 
                                type="tel" 
                                formControlName="phone"
                                [placeholder]="'EVENTS.BOOKING.PLACEHOLDERS.PHONE' | translate"
                                [class.border-red-500]="bookingForm.get('phone')?.invalid && bookingForm.get('phone')?.touched"
                                class="w-full h-11 rounded-lg border-2 border-gray-300 shadow-sm sm:text-sm px-3 transition-colors focus:outline-none focus:ring-2 focus:ring-enduro-orange"
                            >
                            @if (bookingForm.get('phone')?.invalid && bookingForm.get('phone')?.touched) {
                                <p class="text-xs text-red-600 mt-1">
                                    @if (bookingForm.get('phone')?.errors?.['required']) {
                                        {{ 'EVENTS.BOOKING.ERRORS.PHONE_REQUIRED' | translate }}
                                    }
                                    @if (bookingForm.get('phone')?.errors?.['invalidPhone']) {
                                        {{ 'EVENTS.BOOKING.ERRORS.INVALID_PHONE' | translate }}
                                    }
                                </p>
                            }
                        </div>

                        <!-- Participants -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">
                                {{ 'EVENTS.BOOKING.PARTICIPANTS' | translate }} <span class="text-red-600">*</span>
                            </label>
                            <input 
                                type="number" 
                                formControlName="participants"
                                [max]="availableSpots()"
                                min="1"
                                class="w-full h-11 rounded-lg border-2 border-gray-300 shadow-sm sm:text-sm px-3 focus:outline-none focus:ring-2 focus:ring-enduro-orange"
                            >
                            <p class="text-xs text-gray-500 mt-1 font-medium">
                                Posti disponibili: <span class="text-enduro-orange">{{ availableSpots() }}</span>
                            </p>
                            @if (bookingForm.get('participants')?.errors?.['max']) {
                                <p class="text-xs text-red-600 mt-1">
                                    Massimo {{ availableSpots() }} partecipanti disponibili
                                </p>
                            }
                        </div>
                    </div>

                    <!-- Fiscal Code -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            {{ 'EVENTS.BOOKING.FISCAL_CODE' | translate }} <span class="text-red-600">*</span>
                        </label>
                        <input 
                            type="text" 
                            formControlName="fiscal_code"
                            [placeholder]="'EVENTS.BOOKING.PLACEHOLDERS.FISCAL_CODE' | translate"
                            [class.border-red-500]="bookingForm.get('fiscal_code')?.invalid && bookingForm.get('fiscal_code')?.touched"
                            class="w-full h-11 rounded-lg border-2 border-gray-300 shadow-sm sm:text-sm px-3 uppercase transition-colors focus:outline-none focus:ring-2 focus:ring-enduro-orange"
                        >
                        @if (bookingForm.get('fiscal_code')?.invalid && bookingForm.get('fiscal_code')?.touched) {
                            <p class="text-xs text-red-600 mt-1">
                                @if (bookingForm.get('fiscal_code')?.errors?.['required']) {
                                    {{ 'EVENTS.BOOKING.ERRORS.FISCAL_CODE_REQUIRED' | translate }}
                                }
                                @if (bookingForm.get('fiscal_code')?.errors?.['minlength'] || bookingForm.get('fiscal_code')?.errors?.['maxlength']) {
                                    {{ 'EVENTS.BOOKING.ERRORS.FISCAL_CODE_LENGTH' | translate }}
                                }
                            </p>
                        }
                    </div>

                  </form>

                  @if (backendError()) {
                    <div class="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">
                      <div class="flex items-start">
                        <svg class="h-5 w-5 text-red-400 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd" />
                        </svg>
                        <span class="font-medium">{{ backendError() }}</span>
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="bg-gray-50 px-4 pb-4 pt-3 sm:px-6 rounded-b-2xl">
            <div class="sm:flex sm:justify-end sm:items-center sm:ml-14">
              <button 
                  type="submit" 
                  (click)="onSubmit()" 
                  [disabled]="submitting() || bookingForm.invalid"
                  class="inline-flex w-full justify-center rounded-md bg-enduro-orange px-8 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-opacity-90 sm:w-auto transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                  @if (submitting()) {
                    {{ 'EVENTS.BOOKING.SUBMITTING' | translate }}
                  } @else {
                    {{ 'EVENTS.BOOKING.SUBMIT' | translate }}
                  }
              </button>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  `
})
export class EventBookingModalComponent implements OnInit {
  @Input() event: any;
  @Output() closed = new EventEmitter<void>();
  @Output() booked = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private api = inject(ApiService);
  private notification = inject(NotificationService);
  private translate = inject(TranslateService);

  submitting = signal(false);
  backendError = signal('');
  bookingForm!: FormGroup;

  availableSpots = signal<number>(20);

  ngOnInit() {
    // Calculate available spots
    const maxParticipants = this.event?.max_participants || 20;
    const currentParticipants = this.event?.current_participants || 0;
    this.availableSpots.set(Math.max(0, maxParticipants - currentParticipants));

    this.bookingForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2), this.noNumbersValidator]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required, this.phoneValidator]],
      participants: [1, [Validators.required, Validators.min(1), Validators.max(this.availableSpots())]],
      fiscal_code: ['', [Validators.required, Validators.minLength(11), Validators.maxLength(16)]]
    });
  }

  // Custom validator: no numbers in name
  noNumbersValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    return /\d/.test(control.value) ? { hasNumbers: true } : null;
  }

  // Custom validator: phone must have at least 10 digits
  phoneValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const cleanPhone = control.value.replace(/[\s\-\(\)\.]/g, '');
    const digitsOnly = cleanPhone.replace(/\+/g, '');

    // Check if contains only digits and optional +
    if (!/^[\+]?[0-9]+$/.test(cleanPhone)) {
      return { invalidPhone: true };
    }

    // Check minimum 10 digits
    if (digitsOnly.length < 10) {
      return { invalidPhone: true };
    }

    return null;
  }

  closeModal() {
    this.closed.emit();
  }

  onSubmit() {
    // Mark all fields as touched to show errors
    Object.keys(this.bookingForm.controls).forEach(key => {
      this.bookingForm.get(key)?.markAsTouched();
    });

    if (this.bookingForm.invalid) {
      this.translate.get('EVENTS.BOOKING.ERRORS.MISSING_FIELDS').subscribe(res => {
        this.backendError.set(res || 'Compila correttamente tutti i campi obbligatori');
      });
      return;
    }

    if (this.submitting() || !this.event) return;

    this.submitting.set(true);
    this.backendError.set(''); // Clear previous errors

    const stravaToken = localStorage.getItem('strava_token');
    let stravaAthleteId = null;
    if (stravaToken) {
      try {
        const tokenData = JSON.parse(stravaToken);
        stravaAthleteId = tokenData.athlete_id;
      } catch (e) {
        console.error('Error parsing strava_token', e);
      }
    }

    this.api.post('events/book', {
      event_id: this.event.id,
      ...this.bookingForm.value,
      strava_athlete_id: stravaAthleteId
    }).subscribe({
      next: () => {
        this.translate.get('EVENTS.BOOKING.SUCCESS').subscribe(res => {
          this.notification.showSuccess(res || 'Prenotazione effettuata con successo!');
        }); // Assuming there is a success key or create one. Using fallback for now implies verifying keys later.
        // Actually EVENTS.BOOKING.SUCCESS doesn't exist in json you viewed. 
        // View 'it.json' again? No, I saw it. 
        // EVENTS.BOOKING.ERRORS exists. common success message?
        // I will use hardcoded fallback or better:
        this.notification.showSuccess('Prenotazione effettuata con successo!');

        this.submitting.set(false);
        this.backendError.set('');
        this.booked.emit();
        this.closeModal();
      },
      error: (err) => {
        console.error(err);
        const backendErrorMsg = err.error?.error || '';

        if (backendErrorMsg.includes('Invalid email domain')) {
          this.translate.get('EVENTS.BOOKING.ERRORS.INVALID_EMAIL_DOMAIN').subscribe(res => {
            this.backendError.set(res);
          });
        } else if (backendErrorMsg.includes('Missing required fields')) {
          this.translate.get('EVENTS.BOOKING.ERRORS.MISSING_FIELDS').subscribe(res => {
            this.backendError.set(res);
          });
        } else if (backendErrorMsg.includes('phone')) {
          this.translate.get('EVENTS.BOOKING.ERRORS.INVALID_PHONE').subscribe(res => {
            this.backendError.set(res);
          });
        } else {
          this.translate.get('EVENTS.BOOKING.ERRORS.GENERIC_ERROR').subscribe(res => {
            this.backendError.set(res);
          });
        }

        this.submitting.set(false);
      }
    });
  }
}
